import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, MoreVertical, Trash2, FileText } from 'lucide-react';
import {
    getBroadcastTemplates,
    deleteBroadcastTemplate,
    type BroadcastTemplate,
} from '@/services/api/broadcast-templates';

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

function BlockPreview({ template }: { template: BroadcastTemplate }) {
    const blocks = (template.template_params as { blocks?: Array<{ type: string; title?: string; message?: string }> }).blocks ?? [];
    if (!blocks.length) return <span className="text-muted-foreground text-xs">—</span>;
    const first = blocks[0];
    if (first.type === 'text') return <span className="text-xs text-muted-foreground truncate">{first.message ?? '—'}</span>;
    return <span className="text-xs text-muted-foreground truncate">{first.title ?? '—'}</span>;
}

export default function BroadcastTemplates() {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [viewTemplate, setViewTemplate] = useState<BroadcastTemplate | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<BroadcastTemplate | null>(null);

    const { data: templates = [], isLoading } = useQuery({
        queryKey: ['broadcast-templates'],
        queryFn: getBroadcastTemplates,
        staleTime: 1000 * 60,
    });

    const { mutate: remove, isPending: deleting } = useMutation({
        mutationFn: (id: number) => deleteBroadcastTemplate(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['broadcast-templates'] });
            toast({ title: 'Template deletado.' });
            setDeleteTarget(null);
        },
        onError: () => {
            toast({ title: 'Erro ao deletar template.', variant: 'destructive' });
        },
    });

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Templates de Broadcast</h1>
                    <p className="text-sm text-muted-foreground">Reutilize conteúdo de broadcasts anteriores</p>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                    <FileText className="h-8 w-8" />
                    <p className="text-sm">Nenhum template salvo.</p>
                </div>
            ) : (
                <div className="rounded-md border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/50">
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Título</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">Prévia</th>
                                <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">Criado em</th>
                                <th className="px-4 py-3 w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map((t) => (
                                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                                    <td className="px-4 py-3 font-medium">{t.title}</td>
                                    <td className="px-4 py-3 hidden md:table-cell max-w-xs">
                                        <BlockPreview template={t} />
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                                        {formatDate(t.created_at)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-7 w-7">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => setViewTemplate(t)}>
                                                    <FileText className="h-4 w-4 mr-2" />
                                                    Ver detalhes
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="text-destructive focus:text-destructive"
                                                    onClick={() => setDeleteTarget(t)}
                                                >
                                                    <Trash2 className="h-4 w-4 mr-2" />
                                                    Deletar
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={!!viewTemplate} onOpenChange={(o) => !o && setViewTemplate(null)}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{viewTemplate?.title}</DialogTitle>
                    </DialogHeader>
                    <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                        {viewTemplate ? JSON.stringify(viewTemplate.template_params, null, 2) : ''}
                    </pre>
                </DialogContent>
            </Dialog>

            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Deletar template?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        "{deleteTarget?.title}" será removido permanentemente.
                    </p>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
                        <Button
                            variant="destructive"
                            disabled={deleting}
                            onClick={() => deleteTarget && remove(deleteTarget.id)}
                        >
                            {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Deletar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
